import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Search, ExternalLink } from "lucide-react";

export default function Startups() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [sector, setSector] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/startups/sectors").then((r) => setSectors(["All", ...r.data])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (sector !== "All") params.sector = sector;
    if (search.trim()) params.search = search.trim();
    api.get("/startups", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [sector, search]);

  const grouped = useMemo(() => {
    const counts = {};
    items.forEach((s) => { counts[s.sector] = (counts[s.sector] || 0) + 1; });
    return counts;
  }, [items]);

  return (
    <div className="pb-24">
      <section className="border-b border-ink/10 py-16" aria-labelledby="startups-title">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="tag mb-3">{t("startups.eyebrow")}</div>
          <h1 id="startups-title" className="font-display font-black text-ink text-5xl sm:text-6xl leading-[1]">
            {t("startups.title_a")} <span className="text-teal">{t("startups.title_b")}</span>.
          </h1>
          <p className="mt-6 text-ink/70 max-w-2xl leading-relaxed">{t("startups.intro")}</p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3" aria-label={t("startups.sectors")}>
            <div className="tag mb-4">{t("startups.sectors")}</div>
            <div className="flex lg:flex-col flex-wrap gap-2 max-h-[70vh] lg:overflow-y-auto pr-2">
              {sectors.map((s) => (
                <button
                  key={s}
                  data-testid={`sector-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setSector(s)}
                  aria-pressed={sector === s}
                  className={`px-4 py-2 text-sm text-left border transition-colors ${
                    sector === s
                      ? "bg-brand border-teal text-ink"
                      : "bg-white border-ink/15 text-ink/75 hover:border-brand hover:text-brand"
                  }`}
                >
                  {s === "All" ? t("startups.all") : s}
                  {sector === s && grouped[s] && s !== "All" && (
                    <span className="ml-2 font-mono text-[10px] opacity-70">{grouped[s]}</span>
                  )}
                </button>
              ))}
            </div>
          </aside>

          <div className="lg:col-span-9">
            <label htmlFor="startup-search-input" className="sr-only">{t("startups.search_placeholder")}</label>
            <div className="mb-6 flex items-center gap-3 bg-surface border border-ink/10 px-4 py-3">
              <Search className="w-4 h-4 text-ink/50" aria-hidden="true" />
              <input
                id="startup-search-input"
                data-testid="startup-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("startups.search_placeholder")}
                className="flex-1 bg-transparent outline-none text-ink placeholder-ink/40 text-sm"
              />
              <span className="font-mono text-xs text-ink/40" aria-live="polite">{items.length} {t("startups.results")}</span>
            </div>

            {loading ? (
              <div className="text-ink/50 font-mono text-sm" role="status">{t("startups.loading")}</div>
            ) : items.length === 0 ? (
              <div className="text-ink/50 font-mono text-sm" role="status">{t("startups.no_match")}</div>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 list-none p-0" aria-label="Startups">
                {items.map((s) => (
                  <li key={s.id} className="bg-base">
                    <article data-testid={`startup-${s.id}`} className="p-6 card-hover h-full">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-teal">
                          {s.sector}
                        </span>
                        {s.website && (
                          <a href={s.website} target="_blank" rel="noreferrer" className="text-ink/40 hover:text-teal" aria-label={`Visit ${s.name} website`}>
                            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                          </a>
                        )}
                      </div>
                      <h2 className="font-display font-bold text-ink text-lg leading-tight">{s.name}</h2>
                      {s.founders && (
                        <div className="mt-1 text-ink/50 text-xs">{t("startups.founders")}: {s.founders}</div>
                      )}
                      {s.description && (
                        <p className="mt-3 text-ink/70 text-sm leading-relaxed line-clamp-4">{s.description}</p>
                      )}
                    </article>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
